/** @jsxImportSource ./simboluri-jsx */
import { useT } from "../i18n";
import { CatalogPasi, PasCatalog } from "./tipuri";
import HoverCard from "./HoverCard";

// TITLUL unui pas, cu «ce face pasul acesta» la hover.
//
// Înainte, textul stătea de două ori în fiecare componentă de pas: o dată în
// starea «pasul nu a rulat încă» (deci vizibil exact când nu-l citea nimeni)
// și o dată ca secțiune narativă în pagină. La Pasul 6 secțiunea aceea avea
// cinci blocuri desfășurabile și împingea cifrele sub linia de plutire, iar
// cele două texte nici măcar nu spuneau același lucru.
//
// Acum e o intrare per pas în `catalog_pasi.py`, arătată în ambele locuri:
// aici la hover pe titlu (zero spațiu în pagină) și de `DescriereaPasului`
// în starea goală (text obișnuit, fiindcă atunci pagina e oricum liberă).

import { useCatalogPasi } from "./useCatalog";
export { useCatalogPasi };

export function pasDin(cheie: string, catalog: CatalogPasi | null) {
  return catalog?.pasi?.[cheie] ?? null;
}

/** Conținutul explicației — același la hover pe titlu și în pagina goală. */
export function Continut({ p }: { p: PasCatalog }) {
  const t = useT();
  return (
    <>
      <span className="fx-tip-title">
        {t("struct.pas.titlu", { nr: p.pas, nume: p.nume })}
      </span>
      <span className="fx-tip-block">
        <span className="fx-tip-label">{t("struct.fisa.intrebare")}</span>
        <span>{p.intrebarea}</span>
      </span>
      <span className="fx-tip-block">
        <span className="fx-tip-label">{t("struct.fisa.ce_face")}</span>
        <span>{p.ce_face}</span>
      </span>
      {p.primeste?.length > 0 && (
        <span className="fx-tip-block">
          <span className="fx-tip-label">{t("struct.fisa.primeste")}</span>
          <span className="fx-params">
            {p.primeste.map((x, i) => (
              <span key={i} className="tp-rand">
                <span>{x.ce}</span>
                <span className="cv-de-unde">{x.de_unde}</span>
              </span>
            ))}
          </span>
        </span>
      )}
      {p.decide?.length > 0 && (
        <span className="fx-tip-block">
          <span className="fx-tip-label">{t("struct.fisa.decide")}</span>
          <span className="tp-lista">
            {p.decide.map((x, i) => (
              <span key={i}>· {x}</span>
            ))}
          </span>
        </span>
      )}
      {p.verifica?.length > 0 && (
        <span className="fx-tip-block">
          <span className="fx-tip-label">{t("struct.fisa.verifica")}</span>
          <span className="tp-lista">
            {p.verifica.map((x, i) => (
              <span key={i}>· {x}</span>
            ))}
          </span>
        </span>
      )}
      {p.da_mai_departe?.length > 0 && (
        <span className="fx-tip-block">
          <span className="fx-tip-label">{t("struct.fisa.da_mai_departe")}</span>
          <span className="fx-params">
            {p.da_mai_departe.map((x, i) => (
              <span key={i} className="tp-rand">
                <span>{x.ce}</span>
                <span className="cv-de-unde">{x.cui}</span>
              </span>
            ))}
          </span>
        </span>
      )}
      {p.capcane && p.capcane.length > 0 && (
        <span className="fx-tip-block tp-capcane">
          <span className="fx-tip-label">{t("struct.fisa.capcane")}</span>
          <span className="tp-lista">
            {p.capcane.map((x, i) => (
              <span key={i}>· {x}</span>
            ))}
          </span>
        </span>
      )}
    </>
  );
}

/** Titlul <h2> al paginii de pas. */
export default function TitluPas({
  cheie,
  titlu,
}: {
  cheie: string;
  titlu: string;
}) {
  const t = useT();
  const p = pasDin(cheie, useCatalogPasi());
  if (!p) return <h2>{titlu}</h2>;
  return (
    <h2 className="tp-titlu">
      <HoverCard
        latime={520}
        clasaCard="cv-tip"
        eticheta={t("struct.fisa.eticheta", { nr: p.pas })}
        continut={<Continut p={p} />}
      >
        <span className="tp-text">{titlu}</span>
        <span className="tp-semn">{t("struct.fisa.semn")}</span>
      </HoverCard>
    </h2>
  );
}

/** Aceeași explicație, ca text obișnuit — pentru starea «n-a rulat încă». */
export function DescriereaPasului({ cheie }: { cheie: string }) {
  const t = useT();
  const p = pasDin(cheie, useCatalogPasi());
  if (!p) return null;
  return (
    <div className="tp-descriere">
      <p>
        <strong>{p.intrebarea}</strong>
      </p>
      <p>{p.ce_face}</p>
      {p.primeste?.length > 0 && (
        <p className="sg-muted">
          {t("struct.fisa.are_nevoie")}{" "}
          {p.primeste.map((x, i) => (
            <span key={i}>
              {i > 0 && "; "}
              {x.ce} <em>({x.de_unde})</em>
            </span>
          ))}
          .
        </p>
      )}
    </div>
  );
}
